/**
 * Sign in.
 *
 * Email and password, then — if the account requires it — a second factor. The screen never says which
 * half of a wrong credential was wrong, and it never offers to create an account: accounts exist by
 * invitation only (BRIEF §13), and the button to make one lives on an administrator's screen, not here.
 *
 * The one refusal it does explain is a lockout, because the words come from the database's own hook and
 * the right next step ("ask an administrator") is different from "try again".
 */
import { useEffect, useState } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useSession } from '../lib/session';
import { requestPasswordReset, signInWithPassword, urlCarriesAuthToken } from '../lib/auth';
import { Button } from '../ui/primitives';
import { Field, Input } from '../ui/forms';
import { AuthError, AuthFrame } from './AuthFrame';

export function SignInScreen() {
  const { hasAuthSession, isLoading } = useSession();
  const location = useLocation();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const [mode, setMode] = useState<'sign-in' | 'reset' | 'reset-sent'>('sign-in');

  // An invitation or recovery link lands on the site root with a token in the fragment. supabase-js
  // exchanges it on load; the session then exists and the invitation screen takes over.
  useEffect(() => {
    if (urlCarriesAuthToken()) setMode('sign-in');
  }, []);

  if (hasAuthSession && !isLoading) {
    const from = (location.state as { from?: string } | null)?.from ?? '/';
    return <Navigate to={from} replace />;
  }

  if (mode === 'reset-sent') {
    return (
      <AuthFrame
        title="Check your email"
        subtitle="If that address belongs to an account, a link to choose a new password is on its way."
        aside="The message is worded the same way whether or not the address exists, so nobody can use this form to find out who has an account."
      >
        <Button onClick={() => setMode('sign-in')}>Back to sign in</Button>
      </AuthFrame>
    );
  }

  if (mode === 'reset') {
    return (
      <AuthFrame title="Forgotten password" subtitle="Enter the address your account uses.">
        <form
          noValidate
          onSubmit={async (event) => {
            event.preventDefault();
            setBusy(true);
            try {
              await requestPasswordReset(email);
              setMode('reset-sent');
            } finally {
              setBusy(false);
            }
          }}
          className="space-y-4"
        >
          <Field label="Email" required>
            <Input type="email" autoComplete="username" value={email} onChange={(e) => setEmail(e.target.value)} height="form" autoFocus />
          </Field>
          <div className="flex items-center justify-between">
            <Button type="button" variant="ghost" onClick={() => setMode('sign-in')} disabled={busy}>
              Back
            </Button>
            <Button type="submit" variant="primary" disabled={busy || !email.includes('@')}>
              {busy ? 'Sending…' : 'Send the link'}
            </Button>
          </div>
        </form>
      </AuthFrame>
    );
  }

  return (
    <AuthFrame
      title="Sign in"
      subtitle="Accounts are created by invitation. If you were invited, use the link in that email first."
      aside="Locked out? An administrator can unlock the account from Administration → Users. There is no self-service unlock, by design."
    >
      <form
        noValidate
        onSubmit={async (event) => {
          event.preventDefault();
          setBusy(true);
          setError(null);
          try {
            await signInWithPassword(email, password);
            // The session listener in SessionProvider takes it from here.
          } catch (err) {
            setError(err);
          } finally {
            setBusy(false);
          }
        }}
        className="space-y-4"
      >
        <Field label="Email" required>
          <Input
            type="email"
            autoComplete="username"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            height="form"
            autoFocus
          />
        </Field>
        <Field label="Password" required>
          <Input
            type="password"
            autoComplete="current-password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            height="form"
          />
        </Field>

        <AuthError error={error} />

        <div className="flex items-center justify-between">
          <button type="button" onClick={() => setMode('reset')} className="text-small text-ink-muted hover:text-ink">
            Forgotten password?
          </button>
          <Button type="submit" variant="primary" disabled={busy || !email || !password}>
            {busy ? 'Signing in…' : 'Sign in'}
          </Button>
        </div>
      </form>
    </AuthFrame>
  );
}
